import * as React from "react";
import { List, Button, EditableText, isNullish } from "@theorgchartdev/storybook";
import SectionCard, {
  SectionCardControls,
  SectionCardGroup
} from "../../../../Composites/SectionCard/SectionCard";
import { GetChartMetadata } from "@/Utils/Promises";
import ActionListItem from "../../../../Composites/ActionListItem/ActionListItem";
import * as StateLib from "@/Utils/StateLib";
import { appstr } from "@coreapp/utils/appstr";
import { CopyBaseChartPopover } from "@/appComponents/Blocks/Modals/SettingsModal/AccountSettings/ChartManagement/CopyBaseChartPopover";
import { DeleteBaseChartPopover } from "@/appComponents/Blocks/Modals/SettingsModal/AccountSettings/ChartManagement/DeleteBaseChartPopover";
import { createBoldToastMessage } from "@/appComponents/Blocks/Cards/ToastContainer/utils";
import { useToast } from "@/providers/ToastProvider";
import { useChartSettingsContext } from "@/providers/ChartSettingsProvider";
import { IsPositionChart } from "@coreapp/utils/IsPositionChart";
import { usePermissionCheck } from "@/hooks/usePermissionCheck";
import { gWhiteLabel } from "@coreapp/whitelabel/brand";
import { enumBrand } from "@coreapp/constants";

export const ChartManagement = ({ accountConfig, handleUpdate }) => {
  if (!accountConfig) return;

  const [selectedChartID, setSelectedChartID] = React.useState(null);
  const { setShowCreateChartModal, launchChartSettings } = useChartSettingsContext();
  const { isSupportLogin } = usePermissionCheck();
  const isIsolvedBrand = gWhiteLabel === enumBrand.isolved;
  const canDeleteBaseChart = chartID => !isIsolvedBrand || isSupportLogin || chartID !== "0";

  const { addToast } = useToast();

  React.useEffect(() => {
    let wsMgr = StateLib.GetWSMgr();
    const eventTracker = wsMgr.GetUsageTracker();
    eventTracker.ReportEvent("account_settings.base_charts.opened");
  }, []);

  const handleCreateBaseChart = () => {
    let wsMgr = StateLib.GetWSMgr();
    if (wsMgr && wsMgr.BroadcastTopic) {
      wsMgr.BroadcastTopic("CreateChartModal", true);
    }
  };

  const getNextChartID = () => {
    const existingIDs = accountConfig.charts.map(chart => chart.ID);
    let validID = "0";

    while (existingIDs.includes(validID)) {
      validID = (parseInt(validID) + 1).toString();
    }

    return validID;
  };

  const handleListSelect = chartID => {
    if (chartID !== selectedChartID) setSelectedChartID(chartID);
  };

  const handleInputSave = (value, rename, chartID) => {
    if (value !== null && value !== "") {
      if (rename) {
        const selectedChartIndex = accountConfig.charts.findIndex(chart => chart.ID === chartID);

        if (selectedChartIndex !== -1) {
          const updatedCharts = [...accountConfig.charts];
          updatedCharts[selectedChartIndex] = {
            ...updatedCharts[selectedChartIndex],
            name: value
          };

          const updatedAccountConfig = {
            ...accountConfig,
            charts: updatedCharts
          };

          handleUpdate(updatedAccountConfig, "charts");
        }
      }
      setSelectedChartID(null);
    }
  };

  const handleRenameChart = (value, eventInfo) => {
    if (isNullish(eventInfo?.chartID)) return;
    handleInputSave(value, true, eventInfo.chartID);
  };

  const handleChangesToChart = (e, chartID) => {
    const updatedAccountConfig = { ...accountConfig };
    let dataMode = "charts";
    switch (e) {
      case "set-default":
        updatedAccountConfig.defaultchart = chartID;
        dataMode = "defaultchart";
        break;
      case "settings":
        launchChartSettings(chartID);
        break;
      case "delete":
        let chartIdx = updatedAccountConfig.charts.findIndex(chart => chart.ID === chartID);
        const chart = updatedAccountConfig.charts[chartIdx];
        const wsMgr = StateLib.GetWSMgr();
        const doc = wsMgr?.GetDoc();
        const openChartID = doc?.GetChartID();
        if (openChartID === chartID) {
          let alertMsg = appstr("deleteOpenConfirm1");
          wsMgr.CreateReactAlert(
            appstr("Delete open chart"),
            alertMsg,
            () => {
              updatedAccountConfig.charts.splice(chartIdx, 1);
              handleUpdate(updatedAccountConfig, dataMode);
              addToast({
                ...createBoldToastMessage("Entity deleted", { entity: chart.name }),
                status: "success",
                timeoutMS: 5000
              });
            },
            () => {},
            appstr("OK"),
            appstr("Cancel")
          );
        } else {
          updatedAccountConfig.charts.splice(chartIdx, 1);
          addToast({
            ...createBoldToastMessage("Entity deleted", { entity: chart.name }),
            status: "success",
            timeoutMS: 5000
          });
        }
        break;
      default:
        break;
    }
    handleUpdate(updatedAccountConfig, dataMode);
  };

  const getNextChart = (existingChart, chartToCopy) => {
    //Copy new reserved values
    existingChart["ID"] = chartToCopy["ID"];
    existingChart["name"] = chartToCopy["name"];
    //Null out chart references to collections - these are no longer valid
    if (IsPositionChart(existingChart)) {
      existingChart["positionsCollection"] = null;
      existingChart["holdersCollection"] = null;
    } else {
      existingChart["employeesCollection"] = null;
    }
    existingChart["subchartsCollection"] = null;
    //Wipe a number of other things that shouldn't be copied.
    existingChart["sync"] = {};
    existingChart["Refreshtime"] = null;
    existingChart["Timestamp"] = null;
    existingChart["description"] = ""; //TODO: Copy?
    return existingChart;
  };

  const handleCopyChart = async chartID => {
    const { result } = await GetChartMetadata(chartID, true);

    // Find the selected chart by ID
    const chartIdx = accountConfig.charts.findIndex(chart => chart.ID === chartID);

    // Check if a valid chart was found
    if (chartIdx !== -1) {
      // Make a copy of the selected chart
      const chartToCopy = result ? result : { ...accountConfig.charts[chartIdx] };
      const nextChart = { ...accountConfig.charts[chartIdx] };
      const copiedName = accountConfig.charts[chartIdx].name;

      // Generate a new name for the copied chart
      const existingChartNames = accountConfig.charts.map(chart => chart.name);
      const validName = existingChartNames.GetUniqueDuplicateName(chartToCopy.name);

      // Update the copied chart with the valid ID and name
      nextChart.ID = getNextChartID();
      nextChart.name = validName;

      // Use the existing chart and copy new chart onto it
      let mergedChart = getNextChart(chartToCopy, nextChart);
      mergedChart.fullChart = true;

      // Add the copied chart to the chart list
      const updatedAccountConfig = {
        ...accountConfig,
        charts: [...accountConfig.charts, mergedChart]
      };

      handleUpdate(updatedAccountConfig, "charts");

      addToast({
        ...createBoldToastMessage("Entity copied", { entity: copiedName }),
        status: "success",
        timeoutMS: 5000
      });
    }
  };

  const BuildChartList = () => {
    const charts = [...accountConfig.charts];
    const defaultId = accountConfig.defaultchart;
    const selectedId = selectedChartID;

    return charts.map(chart => {
      const isDefault = chart.ID === defaultId;
      const isSelected = chart.ID === selectedId;
      return {
        content: (
          <ActionListItem
            padding="medium"
            contentBefore={
              <EditableText
                className="oc-fill-width"
                size={"medium"}
                bold={false}
                text={chart.name}
                onChange={handleRenameChart}
                eventInfo={{ chartID: chart.ID }}
                stopPropogation={true}
                dataTestId={`account-settings-base-charts-row-${chart.ID}-rename-input`}
              />
            }
            actions={[
              {
                iconName: isDefault ? "star-solid" : "star-regular",
                iconProps: {
                  color: "brand"
                },
                checkActive: () => isDefault || isSelected,
                onClick: () => handleChangesToChart("set-default", chart.ID),
                tooltipProps: { message: appstr("Set as default chart") },
                dataTestId: `account-settings-base-charts-row-${chart.ID}-set-default-btn`
              },
              {
                iconName: "pen-to-square",
                dataTestId: `configure-chart-btn-${chart.name}`,
                checkActive: () => isSelected,
                onClick: () => handleChangesToChart("settings", chart.ID),
                tooltipProps: { message: appstr("Open in chart settings") }
              },
              {
                checkActive: () => isSelected,
                content: (
                  <CopyBaseChartPopover
                    handleCopy={() => handleCopyChart(chart.ID)}
                    dataTestId={`copy-chart-btn-${chart.name}`}
                  />
                )
              },
              {
                checkActive: () => isSelected,
                content: (
                  <DeleteBaseChartPopover
                    handleDelete={() => handleChangesToChart("delete", chart.ID)}
                    dataTestId={`delete-chart-btn-${chart.name}`}
                    disabled={!canDeleteBaseChart(chart.ID)}
                    tooltipMessage={
                      canDeleteBaseChart(chart.ID)
                        ? undefined
                        : appstr("Delete chart disabled isolved")
                    }
                  />
                )
              }
            ]}
          />
        ),
        padding: false,
        value: chart.ID,
        dataTestId: `chart-option-${chart.name}`,
        clickHandler: e => handleListSelect(e.value)
      };
    });
  };

  const numCharts = accountConfig?.charts?.length ?? 0;
  return (
    <SectionCardGroup>
      <SectionCardControls>
        <Button
          dataTestId="chart-management-action-create-chart"
          modifier={"secondary"}
          className={"oc-special-secondary"}
          iconProps={{ name: "plus" }}
          onClick={handleCreateBaseChart}
          disabled={numCharts >= 25}
        >
          {appstr("Create base chart")}
        </Button>
      </SectionCardControls>
      <SectionCard title={appstr("Chart Management")}>
        <List
          enableVirtualization={false}
          options={BuildChartList()}
          listWidth="fill"
          showCheck={false}
          value={selectedChartID}
        />
      </SectionCard>
    </SectionCardGroup>
  );
};
